import { useState, useEffect, useRef } from 'react';
import { Drawer, Search, MLPVersion, Breakpoints, useBreakpoint, useTranslation } from '@nextgen-web-framework/all';
import { DynamicObjectInterface } from '@commerce-profile-webcomponents/sdk';
import { SearchDrawerProps } from './types/propTypes';
import styles from './SearchDrawer.module.scss';

const SearchDrawer = ({
  title,
  fieldName,
  data,
  config,
  onClose,
  callback,
  selectedValue,
  isLoading,
}: SearchDrawerProps) => {
  const t = useTranslation();
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [results, setResults] = useState<DynamicObjectInterface>();
  const inputRef = useRef(null);
  const viewportType = useBreakpoint();

  useEffect(() => {
    let returnFn = () => {
      // empty function
    };
    if ([Breakpoints.XS, Breakpoints.SM].includes(viewportType as Breakpoints)) {
      const spanElement = inputRef.current as unknown as HTMLSpanElement;
      const inputElement = spanElement?.querySelector('input') as HTMLInputElement;

      if (inputElement) {
        const updateDrawerHeight = (height?: string, clickedOnItem = false) => {
          const div = document.getElementById('address-search-drawer-id');
          if (div) {
            const rect = div.getBoundingClientRect();

            const visibleHeight = Math.min(rect.height, window.innerHeight - rect.top);
            div.style.height = height || `${visibleHeight}px`;
            div.style.overflowY = height ? 'unset' : 'auto';
          }

          const drawerElement = document.getElementById('nextgen-drawer-component');
          if (drawerElement) {
            if (height) {
              if (!clickedOnItem) {
                drawerElement.removeAttribute('style');
                drawerElement.style.bottom = `0`;
              }
            } else {
              drawerElement.style.top = `32px`;
              drawerElement.style.bottom = `unset`;
            }
          }
        };

        const handleFocus = () => {
          updateDrawerHeight();
        };

        const handleBlur = (event: FocusEvent) => {
          const target = event.relatedTarget;
          const items = document.querySelectorAll(
            `.${styles['search-item']}, div > span[data-testid="close-icon-testid"]`,
          );
          updateDrawerHeight(
            '100%',
            Array.from(items ?? []).some((item) => item === target),
          );
        };

        inputElement.addEventListener('focus', handleFocus);
        inputElement.addEventListener('blur', handleBlur);

        returnFn = () => {
          inputElement.removeEventListener('focus', handleFocus);
          inputElement.removeEventListener('blur', handleBlur);
        };
      }
    }
    return returnFn;
  }, []);

  useEffect(() => {
    const sortedData = data.sort(sortByLabel);
    setResults(sortedData);
  }, [data]);

  const sortByLabel = (a: DynamicObjectInterface, b: DynamicObjectInterface) =>
    a[config?.label].localeCompare(b[config?.label]);

  const searchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);

    const filteredResults = data
      .filter(
        (item: DynamicObjectInterface) =>
          item[config?.label].toLocaleLowerCase().includes(e.target.value.toLocaleLowerCase()) === true,
      )
      .sort(sortByLabel);

    setResults(filteredResults);
  };

  const onItemClick = (item: DynamicObjectInterface) => {
    callback?.(fieldName, item);
    setResults([]);
    setSearchKeyword('');
    onClose?.();
  };

  const onClearHandler = () => {
    setSearchKeyword('');
    setResults(data);
  };

  return (
    <Drawer
      header={`${title}`}
      closeIcon
      onClose={() => onClose?.()}
      bottomGap={0}
      useReactPortal={true}
      portalContainerId={'react-portal-drawer'}
      subHeader={
        <div className={styles['search-box']}>
          <Search
            mlpVersion={MLPVersion.TWO}
            onChange={searchOnChange}
            value={searchKeyword}
            onSearch={() => null}
            onClear={onClearHandler}
            ref={inputRef}
          />
        </div>
      }
    >
      <div
        className={styles['address-search-drawer']}
        id="address-search-drawer-id"
        data-testid="address-search-drawer-testid"
      >
        <div className={styles['search-results']}>
          {results?.map((item: DynamicObjectInterface) => {
            let selectedClass = '';

            if (selectedValue === item?.[config?.key]) {
              selectedClass = styles['selected'];
            }

            return (
              <div
                onClick={() => onItemClick(item)}
                tabIndex={-1}
                aria-hidden="true"
                data-testId="search-list"
                className={`${styles['search-item']} ${selectedClass}`}
                key={item.code}
              >
                {item[config.label]}
              </div>
            );
          })}
          {results?.length === 0 && !isLoading && <>{t('profile.common.noRecord')}</>}
          {isLoading && <>{t('web.loader.title')}</>}
        </div>
      </div>
    </Drawer>
  );
};

export default SearchDrawer;
